import React, { useContext } from "react";

import { estimateInvoiceTotal } from "components/Payment/UpgradeFlowComponents/BillingTab";
import { UserContext } from "layouts/SlapshotMainLayout";
import { Subscription } from "models/User";
import { formatDate, formatPrice, formatSize } from "utils/helper";

import styles from "./UsageTab.module.scss";

interface UsageTabProps {
  subscription: Subscription;
}

const UsageTab: React.FC<UsageTabProps> = ({ subscription }) => {
  return (
    <div className={styles.container}>
      {subscription && (
        <>
          <div className={styles.boxes}>
            <div className={styles.box}>
              <div className={styles.boxHeader}>Asset Storage Usage</div>
              <div>
                <span className={styles.boxPrimaryValue}>
                  {formatSize(subscription.storage_used)}
                </span>
                <span
                  className={styles.boxSecondaryValue}
                >{` / ${formatSize(subscription.plan.storage, "GB")}`}</span>
              </div>
            </div>

            <div className={styles.box}>
              <div className={styles.boxHeader}>Exported Frames</div>
              <div>
                <span className={styles.boxPrimaryValue}>
                  {subscription.frames_used}
                </span>
                <span className={styles.boxSecondaryValue}>
                  {` / ${subscription.frames_limit}`}
                </span>
              </div>
            </div>

            <div className={styles.box}>
              <div className={styles.boxHeader}>Autopilot exported Frames</div>
              <div>
                <span className={styles.boxPrimaryValue}>
                  {subscription.autopilot_frames_used}
                </span>
                <span className={styles.boxSecondaryValue}>
                  {` / ${subscription.autopilot_frames_limit}`}
                </span>
              </div>
            </div>

            <div className={styles.box}>
              <div className={styles.boxHeader}>Number of Seats</div>
              <div>
                <span className={styles.boxPrimaryValue}>Unlimited</span>
              </div>
            </div>

            <div className={styles.box}>
              <div className={styles.boxHeader}>Current Monthly Total</div>
              <div>
                <span className={styles.boxPrimaryValue}>
                  {subscription.renewal_date &&
                  !!subscription.plan.price.unit_amount_decimal
                    ? `${formatPrice({ currency: subscription.plan.price.currency, unit_amount_decimal: estimateInvoiceTotal(subscription) })}`
                    : "--"}
                </span>
              </div>
            </div>
          </div>

          {subscription?.usage && !!Object.keys(subscription.usage).length && (
            <div className={styles.usageCon}>
              <div className={styles.usageTableCon}>
                <table border={1} className={styles.usageTable}>
                  <thead>
                    <tr>
                      <th>From</th>
                      <th>To</th>
                      <th>Number of Seats</th>
                      <th>Assets Storage Used</th>
                      <th>Inference Frames Exported</th>
                      <th>Autopilot Frames Exported</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr key="Present">
                      <td>
                        {formatDate(
                          Object.keys(subscription.usage).sort(
                            (a, b) =>
                              new Date(b).getTime() - new Date(a).getTime(),
                          )[0] || "--",
                        )}
                      </td>
                      <td>Present</td>
                      <td>Unlimited</td>
                      <td>{formatSize(subscription.storage_used)}</td>
                      <td>{subscription.frames_used}</td>
                      <td>{subscription.autopilot_frames_used}</td>
                    </tr>

                    {Object.keys(subscription.usage)
                      .sort(
                        (a, b) => new Date(b).getTime() - new Date(a).getTime(),
                      )
                      .map((date, index, sortedDates) => {
                        const usage = subscription.usage?.[date];
                        const from =
                          index === sortedDates.length - 1
                            ? "--"
                            : formatDate(sortedDates[index + 1]);
                        const to = formatDate(date);

                        if (!usage) return null;
                        return (
                          <tr key={date}>
                            <td>{from}</td>
                            <td>{to}</td>
                            <td>Unlimited</td>
                            <td>{formatSize(usage.storage_used)}</td>
                            <td>{usage.frames_used}</td>
                            <td>{usage.autopilot_frames_used || 0}</td>
                          </tr>
                        );
                      })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default UsageTab;
